import React, { useMemo, useState } from "react";
import { Button, Empty, Input, Select, Table, Tag } from "antd";
import { RiSearchLine, RiEditLine, RiDeleteBinLine } from "react-icons/ri";
import { languageLabel, templateBody } from "./TemplateUtils";
import styles from "./templates.module.css";

export default function TemplateList({
  templates,
  loading,
  onCreate,
  onEdit,
  onDelete,
}) {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [language, setLanguage] = useState("");

  const categories = useMemo(
    () => [...new Set((templates || []).map((t) => t.category).filter(Boolean))],
    [templates]
  );
  const languages = useMemo(
    () => [...new Set((templates || []).map((t) => t.language).filter(Boolean))],
    [templates]
  );

  const data = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (templates || []).filter((t) => {
      const haystack = `${t.name || ""} ${t.category || ""} ${t.language || ""} ${templateBody(t)}`.toLowerCase();
      return (
        (!q || haystack.includes(q)) &&
        (!category || t.category === category) &&
        (!language || t.language === language)
      );
    });
  }, [templates, search, category, language]);

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <Input
          prefix={<RiSearchLine />}
          placeholder="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={styles.search}
          allowClear
        />
        <Select
          placeholder="Category"
          value={category || undefined}
          onChange={(v) => setCategory(v || "")}
          allowClear
          className={styles.filter}
          options={categories.map((c) => ({ value: c, label: c }))}
        />
        <Select
          placeholder="Language"
          value={language || undefined}
          onChange={(v) => setLanguage(v || "")}
          allowClear
          className={styles.filter}
          options={languages.map((l) => ({ value: l, label: languageLabel(l) }))}
        />
      </div>

      <Table
        rowKey={(t) => t.id || `${t.name}-${t.language}`}
        dataSource={data}
        loading={loading}
        locale={{ emptyText: <Empty description="No templates" /> }}
        pagination={{ pageSize: 25, showSizeChanger: false }}
        scroll={{ x: 900 }}
        className={styles.table}
        columns={[
          {
            title: "Template name",
            dataIndex: "name",
            sorter: (a, b) => String(a.name).localeCompare(String(b.name)),
          },
          {
            title: "Category",
            dataIndex: "category",
            width: 150,
          },
          {
            title: "Language",
            dataIndex: "language",
            width: 190,
            render: (v, row) => (
              <div>
                <div>{languageLabel(v)}</div>
                <small className={styles.muted}>{templateBody(row).slice(0, 48)}</small>
              </div>
            ),
          },
          {
            title: "Status",
            dataIndex: "status",
            width: 190,
            render: (v) => (
              <Tag color={String(v).toUpperCase() === "APPROVED" ? "green" : "default"}>
                {v || "UNKNOWN"}
              </Tag>
            ),
          },
          {
            title: "Last edited",
            dataIndex: "last_updated_time",
            width: 160,
            render: (v) => (v ? new Date(v).toLocaleDateString() : "--"),
          },
          {
            title: "",
            width: 150,
            render: (_, row) => (
              <div className={styles.rowActions}>
                <Button size="small" icon={<RiEditLine />} onClick={() => onEdit(row)} />
                <Button size="small" danger icon={<RiDeleteBinLine />} onClick={() => onDelete(row)} />
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
